package info.rezident.app.data.quiz

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.FirebaseFirestoreException
import dev.gitlive.firebase.firestore.firestore
import info.rezident.app.core.Logger
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.withContext

// Current template version for new quizzes
const val CURRENT_TEMPLATE_VERSION = 3

/**
 * Repository for Quiz template data, handling read-only operations
 * for public quiz structure (questions, steps).
 */
object QuizTemplateRepository {
    private val firestore: FirebaseFirestore = Firebase.firestore

    private val _questions = MutableStateFlow<List<QuizQuestion>>(emptyList())
    val questions: StateFlow<List<QuizQuestion>> = _questions.asStateFlow()

    private val _steps = MutableStateFlow<List<QuizStep>>(emptyList())
    val steps: StateFlow<List<QuizStep>> = _steps.asStateFlow()

    /**
     * Fetches all quiz questions from Firestore and updates the questions flow.
     * Uses a background thread for the operation.
     *
     * @param templateId The template ID to fetch questions for (defaults to CURRENT_TEMPLATE_VERSION)
     */
    suspend fun fetchQuestions(templateId: Int = CURRENT_TEMPLATE_VERSION) = withContext(Dispatchers.Default) {
        try {
            val questionsCollection = firestore
                .collection("quiz")
                .document(templateId.toString())
                .collection("questions")
                .get()

            // Sort questions by id and convert to domain model
            val sortedQuestions = questionsCollection.documents
                .map { documentSnapshot ->
                    documentSnapshot.data<QuizQuestionModel>().toDomainModel()
                }
                .sortedBy { it.id }

            _questions.update { sortedQuestions }
        } catch (e: FirebaseFirestoreException) {
            Logger.error("Error fetching quiz questions for template $templateId: ${e.message}")
        } catch (e: Exception) {
            Logger.error("Unexpected error: ${e.message}")
        }
    }

    /**
     * Fetches all quiz steps from Firestore and updates the steps flow.
     * Uses a background thread for the operation.
     *
     * @param templateId The template ID to fetch steps for (defaults to CURRENT_TEMPLATE_VERSION)
     */
    suspend fun fetchSteps(templateId: Int = CURRENT_TEMPLATE_VERSION) = withContext(Dispatchers.Default) {
        try {
            val stepsCollection = firestore
                .collection("quiz")
                .document(templateId.toString())
                .collection("steps")
                .get()

            // Sort steps by id and convert to domain model
            val sortedSteps = stepsCollection.documents
                .map { documentSnapshot ->
                    documentSnapshot.data<QuizStepModel>().toDomainModel()
                }
                .sortedBy { it.id }

            _steps.update { sortedSteps }
        } catch (e: FirebaseFirestoreException) {
            Logger.error("Error fetching quiz steps for template $templateId: ${e.message}")
        } catch (e: Exception) {
            Logger.error("Unexpected error: ${e.message}")
        }
    }

    /**
     * Get question or factor text by question ID.
     * First tries to get the factor text, falls back to question text if factor is null.
     *
     * @param questionId The question ID as a string
     * @return The factor or question text, or null if not found
     */
    suspend fun getQuestionOrFactorText(questionId: String): String? {
        val id = questionId.toIntOrNull() ?: return null

        // Ensure questions are loaded
        if (_questions.value.isEmpty()) {
            fetchQuestions()
        }

        val question = _questions.value.find { it.id == id }

        return when {
            question == null -> {
                Logger.warn("Question not found for ID: $questionId")
                null
            }

            !question.factor.isNullOrBlank() -> question.factor
            else -> question.text
        }
    }
}
